import { registerResetFn } from "@shared/reactiveVar/makeFSVar";
import type { Flipper } from "@shared/types/flipper";

/**
 * Module-level Sets that track which experiment features have been sent to the backend.
 *
 * This is intentionally "best-effort" tracking:
 * - trackedFeatures: Features we've successfully sent tracking events for
 * - pendingFeatures: Features with in-flight tracking requests
 *
 * These persist for the browser session (cleared on page refresh) and prevent duplicate
 * tracking calls. The bounded size (number of experiment features) means memory is not a concern.
 */
const trackedFeatures = new Set<string>();
const pendingFeatures = new Set<string>();

registerResetFn(() => {
  trackedFeatures.clear();
  pendingFeatures.clear();
});

export const isFeatureTracked = (feature: string): boolean => {
  return trackedFeatures.has(feature);
};

export const isFeaturePending = (feature: string): boolean => {
  return pendingFeatures.has(feature);
};

export const markFeatureAsTracked = (feature: string): void => {
  trackedFeatures.add(feature);
  pendingFeatures.delete(feature);
};

export const markFeatureAsPending = (feature: string): void => {
  pendingFeatures.add(feature);
};

export const unmarkFeatureAsPending = (feature: string): void => {
  pendingFeatures.delete(feature);
};

/**
 * Returns flippers that need tracking: those that are both requested (in args) AND
 * present in untrackedExperimentFeatures, excluding any already tracked or in-flight.
 *
 * Flipper names and experiment feature names share the same namespace, so string
 * comparison is used to determine which flippers are experiment features.
 */
export const getFeaturesToTrack = (
  args: readonly Flipper[],
  untrackedExperimentFeatures: readonly string[]
): string[] => {
  if (untrackedExperimentFeatures.length === 0) return [];

  const untrackedSet = new Set(untrackedExperimentFeatures);

  return args.filter(flipper => {
    const needsTracking = untrackedSet.has(flipper);
    const alreadyTracked = isFeatureTracked(flipper);
    const isPending = isFeaturePending(flipper);

    return needsTracking && !alreadyTracked && !isPending;
  });
};
